import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Loader2,
  CheckCircle,
  AlertCircle,
  Edit2,
  X,
  Save,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRY_LABELS = {
  US: 'United States',
  UK: 'United Kingdom',
  UAE: 'United Arab Emirates',
  SG: 'Singapore',
  CA: 'Canada',
};
const COUNTRY_ORDER = ['US', 'UK', 'UAE', 'SG', 'CA'];

/**
 * Admin config table for per-state Registered Agent + Mail Forwarding addresses.
 *
 * Pattern mirrors SuperAdminStateFees.jsx (same back link, layout,
 * SuperAdminLayout shell, toast pattern). Edit-only — no create, no delete.
 */
const SuperAdminStateAddresses = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Edit modal state
  const [editing, setEditing] = useState(null); // row being edited, or null
  const [editRa, setEditRa] = useState('');
  const [editMf, setEditMf] = useState('');
  const [saving, setSaving] = useState(false);

  // Seed button state
  const [seeding, setSeeding] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.get(`${API}/admin/state-addresses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.state_addresses || []);
    } catch (err) {
      setError(extractErrorMessage(err) || 'Failed to load state addresses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openEdit = (row) => {
    setEditing(row);
    setEditRa(row.ra_address || '');
    setEditMf(row.mf_address || '');
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setEditRa('');
    setEditMf('');
  };

  const handleSave = async () => {
    if (!editing || saving) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.put(
        `${API}/admin/state-addresses/${editing.state_code}`,
        { ra_address: editRa, mf_address: editMf },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows((prev) =>
        prev.map((r) => (r.state_code === editing.state_code ? data : r))
      );
      showToast(`${editing.state_name} addresses saved`);
      closeEdit();
    } catch (err) {
      showToast(extractErrorMessage(err) || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    if (seeding) return;
    if (
      !window.confirm(
        'Seed missing address regions in THIS environment?\n\n' +
          'Creates rows for any US states or UK/UAE/SG/Canada regions that don\'t ' +
          'exist yet, plus their Registered Agent + Mail Forwarding products. ' +
          'Existing addresses are never overwritten. Safe to run multiple times.'
      )
    )
      return;
    try {
      setSeeding(true);
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.post(
        `${API}/admin/state-addresses/seed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(
        `${data.inserted} new, ${data.skipped} already existed (total: ${data.total})`
      );
      fetchRows();
    } catch (err) {
      showToast(extractErrorMessage(err) || 'Seed failed', 'error');
    } finally {
      setSeeding(false);
    }
  };

  const renderAddressCell = (value) => {
    if (!value || !value.trim()) {
      return <span className="text-gray-500">—</span>;
    }
    return <span className="text-gray-200">{value}</span>;
  };

  return (
    <SuperAdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="superadmin-state-addresses-page">
        {/* Toast */}
        {toast && (
          <div
            className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
              toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
            }`}
            data-testid="state-addresses-toast"
          >
            {toast.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
            <span>{toast.message}</span>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <Link
            to="/superadmin/products"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            data-testid="back-to-products-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Products
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#00E87A33' }}>
              <MapPin className="w-5 h-5" style={{ color: '#00E87A' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">State Addresses</h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Registered Agent and Mail Forwarding addresses per region (US, UK, UAE, Singapore, Canada)
              </p>
            </div>
          </div>

          {isSuperAdmin && (
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-gray-200 border border-gray-700 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="seed-state-addresses-btn"
              title="Create missing state rows (idempotent — never overwrites existing addresses)"
            >
              <RefreshCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} />
              {seeding ? 'Seeding…' : 'Seed State Addresses'}
            </button>
          )}
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="state-addresses-loading">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg" data-testid="state-addresses-error">
            {error}
          </div>
        ) : (
          <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="state-addresses-table">
              <thead className="bg-gray-900/50 text-gray-300">
                <tr>
                  <th className="text-left px-4 py-3 font-medium w-44">State</th>
                  <th className="text-left px-4 py-3 font-medium">RA Address</th>
                  <th className="text-left px-4 py-3 font-medium">MF Address</th>
                  <th className="text-right px-4 py-3 font-medium w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {COUNTRY_ORDER.filter((cc) => rows.some((r) => (r.country || 'US') === cc)).map((cc) => (
                  <React.Fragment key={cc}>
                    <tr className="bg-gray-900/70" data-testid={`country-group-${cc}`}>
                      <td colSpan={4} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#00E87A]">
                        {COUNTRY_LABELS[cc] || cc}
                        <span className="ml-2 text-gray-500 normal-case font-normal">
                          {rows.filter((r) => (r.country || 'US') === cc).length} regions
                        </span>
                      </td>
                    </tr>
                    {rows
                      .filter((r) => (r.country || 'US') === cc)
                      .map((row) => (
                        <tr
                          key={row.state_code}
                          className="border-t border-gray-700/40 hover:bg-gray-800/40 transition-colors"
                          data-testid={`state-row-${row.state_code}`}
                        >
                          <td className="px-4 py-3 text-gray-200">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-gray-500 w-16">{row.state_code}</span>
                              <span>{row.state_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">{renderAddressCell(row.ra_address)}</td>
                          <td className="px-4 py-3">{renderAddressCell(row.mf_address)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => openEdit(row)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 border border-gray-700 hover:bg-gray-700 transition-colors"
                              data-testid={`edit-state-${row.state_code}-btn`}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                      No address rows yet. Click <span className="text-gray-300">Seed State Addresses</span> to create them.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit Modal */}
        {editing && (
          <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={closeEdit}
            data-testid="state-addresses-edit-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-[#161B22] border border-gray-700 rounded-xl w-full max-w-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  {editing.state_name} <span className="text-gray-500 text-sm font-normal">({editing.state_code})</span>
                </h2>
                <button
                  onClick={closeEdit}
                  className="text-gray-400 hover:text-white transition-colors"
                  data-testid="edit-modal-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                    Registered Agent Address
                  </label>
                  <input
                    type="text"
                    value={editRa}
                    onChange={(e) => setEditRa(e.target.value)}
                    placeholder="e.g. 1234 Carey Ave, Cheyenne, WY 82001"
                    className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#00E87A]"
                    data-testid="edit-ra-address-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                    Mail Forwarding Address
                  </label>
                  <input
                    type="text"
                    value={editMf}
                    onChange={(e) => setEditMf(e.target.value)}
                    placeholder="e.g. 5678 Business Blvd, Cheyenne, WY 82001"
                    className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#00E87A]"
                    data-testid="edit-mf-address-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeEdit}
                  disabled={saving}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  data-testid="edit-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-black disabled:opacity-50"
                  style={{ backgroundColor: '#00E87A' }}
                  data-testid="edit-save-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminStateAddresses;
